import React, { FC, memo, useMemo, useRef, useState } from 'react';

import Button, { ButtonSize, ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import Dropdown, {
  DropdownItemDataType,
  DropdownOrientation,
  DropdownPlacement,
} from '@zvuk-b2b/react-uikit/ui/Dropdown';
import IconCalendarDay from '@zvuk-b2b/react-uikit/ui/icons/CalendarDay';
import IconChevronDown from '@zvuk-b2b/react-uikit/ui/icons/ChevronDown';
import IconChevronUp from '@zvuk-b2b/react-uikit/ui/icons/ChevronUp';
import IconClipboardList from '@zvuk-b2b/react-uikit/ui/icons/ClipboardList';

import { DATA_TEST_IDS } from './consts';
import styles from './ReportRequestMenu.module.scss';
import { TEXTS } from './texts';
import type { ReportRequestMenuProps } from './types';

const ReportRequestMenu: FC<ReportRequestMenuProps> = ({ onOpenByDaysReport, onOpenEarlyReport }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const items = useMemo<DropdownItemDataType[]>(
    () => [
      {
        key: DATA_TEST_IDS.MENU_EARLY,
        value: TEXTS.REQUEST_EARLY_REPORT_MENU_ITEM,
        content: (
          <span
            className={styles.itemContent}
            data-testid={DATA_TEST_IDS.MENU_EARLY}>
            <span data-testid={DATA_TEST_IDS.MENU_EARLY_ICON}>
              <IconClipboardList />
            </span>
            {TEXTS.REQUEST_EARLY_REPORT_MENU_ITEM}
          </span>
        ),
        onClick: onOpenEarlyReport,
      },
      {
        key: DATA_TEST_IDS.MENU_BY_DAYS,
        value: TEXTS.REQUEST_BY_DAYS_REPORT_MENU_ITEM,
        content: (
          <span
            className={styles.itemContent}
            data-testid={DATA_TEST_IDS.MENU_BY_DAYS}>
            <span data-testid={DATA_TEST_IDS.MENU_BY_DAYS_ICON}>
              <IconCalendarDay />
            </span>
            {TEXTS.REQUEST_BY_DAYS_REPORT_MENU_ITEM}
          </span>
        ),
        onClick: onOpenByDaysReport,
      },
    ],
    [onOpenByDaysReport, onOpenEarlyReport]
  );

  return (
    <div
      className={styles.container}
      ref={containerRef}>
      <Button
        ref={triggerRef}
        elementId="toolbar_button_add"
        size={ButtonSize.SMALL}
        type={ButtonType.PRIMARY}
        className={styles.trigger}
        onClick={() => setIsOpen((currentValue) => !currentValue)}>
        <span className={styles.triggerContent}>
          <span data-testid={DATA_TEST_IDS.TRIGGER}>{TEXTS.REQUEST_REPORT_BUTTON_TEXT}</span>
          {isOpen ? <IconChevronUp /> : <IconChevronDown />}
        </span>
      </Button>
      {isOpen && triggerRef.current && containerRef.current && (
        <Dropdown
          elementId={DATA_TEST_IDS.MENU}
          anchorNode={triggerRef.current}
          parentNode={containerRef.current}
          orientation={DropdownOrientation.RIGHT}
          placement={DropdownPlacement.AUTO}
          items={items}
          className={styles.dropdown}
          itemClassName={styles.item}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default memo(ReportRequestMenu);
